import MojitoServices from 'mojito/services';
import EventTableTypes from './types';

const EventsUtils = MojitoServices.SportsContent.Events.utils;

/**
 * Utility helpers for EventTable column logic, market-group adaptation,
 * standings transformation, and table data shaping.
 *
 * @class EventTableUtils
 * @name utils
 * @memberof Mojito.Modules.EventTable
 */
const { RANK, ODDS, STANDING_POINTS, PARTICIPANT } = EventTableTypes.COLUMN_TYPES;
const { MISSING_ODDS_DISPLAY } = EventTableTypes;

const isParticipantIconUrl = icon => /^https?:\/\//i.test(String(icon || '').trim());

const preferParticipantIcon = (existingIcon, nextIcon) => {
    const existing = String(existingIcon || '').trim();
    const next = String(nextIcon || '').trim();

    if (!next) {
        return existing;
    }
    if (!existing) {
        return next;
    }
    if (isParticipantIconUrl(next)) {
        return next;
    }
    if (isParticipantIconUrl(existing)) {
        return existing;
    }

    return next;
};

const EVENT_TABLE_COMPACT_STAT_FIELD_KEYS = ['gamesPlayed', 'goalDifference', 'points'];

export default class EventTableUtils {
    /**
     * Checks whether a field key belongs to an odds column.
     *
     * @param {string} key - Table field key.
     * @returns {boolean} True when the key represents odds content.
     */
    static isOddsKey(key) {
        return String(key).toLowerCase().includes(String(ODDS).toLowerCase());
    }

    /**
     * Checks whether a field key represents the standings position.
     *
     * @param {string} key - Table field key.
     * @returns {boolean} True when the key is the standings position field.
     */
    static isRankKey(key) {
        return String(key) === RANK;
    }

    /**
     * Checks whether a field key represents standings points.
     *
     * @param {string} key - Table field key.
     * @returns {boolean} True when the key is the standings points field.
     */
    static isPointsKey(key) {
        return key === STANDING_POINTS;
    }

    /**
     * Checks whether a field key represents the participant column.
     *
     * @param {string} key - Table field key.
     * @returns {boolean} True when the key is the participant field.
     */
    static isParticipantKey(key) {
        return key === PARTICIPANT;
    }

    /**
     * Checks whether a cell value represents missing/unavailable market odds.
     *
     * @param {string|number|null|undefined} value - Raw odds cell value.
     * @returns {boolean} True when odds should be treated as unavailable.
     */
    static isMissingOddsDisplayValue(value) {
        return value === null || value === undefined || value === MISSING_ODDS_DISPLAY;
    }

    /**
     * Normalizes a market id into a string value.
     *
     * @param {string|number|null|undefined} marketId - Raw market id.
     * @returns {string|null} Normalized market id or null when unavailable.
     */
    static normalizeMarketId(marketId) {
        if (marketId === null || marketId === undefined) {
            return null;
        }

        return String(marketId);
    }

    /**
     * Keeps only markets whose parent event is an outright event.
     *
     * League-stage participant links can include match markets (for example MRES) as well as
     * outright markets. The group table must use the parent event type as the boundary because
     * outright markets can have many different market type codes.
     *
     * @param {Array<object>} [markets=[]] - Markets referenced by league standings.
     * @param {Array<object>} [events=[]] - Parent events for the referenced markets.
     * @returns {Array<object>} Markets belonging to outright events.
     */
    static filterOutrightMarkets(markets = [], events = []) {
        const outrightEventIds = new Set(
            (events || [])
                .filter(event => EventsUtils.isOutrightEventType(event?.eventType))
                .map(event => String(event.id))
        );

        return (markets || []).filter(market => {
            const eventId = market?.eventNodeId ?? market?.eventId;
            return eventId !== null && eventId !== undefined
                ? outrightEventIds.has(String(eventId))
                : false;
        });
    }

    /**
     * Determines whether a column definition spans multiple row fields.
     *
     * @param {object} [column={}] - Column definition object.
     * @returns {boolean} True when the column contains a `keys` array.
     */
    static isCompoundColumn(column = {}) {
        return Array.isArray(column.keys);
    }

    /**
     * Returns the field keys represented by a column definition.
     *
     * @param {object} [column={}] - Column definition object.
     * @returns {Array<string>} Field keys rendered for the column.
     */
    static getColumnKeys(column = {}) {
        return EventTableUtils.isCompoundColumn(column) ? column.keys : [column.key];
    }

    /**
     * Resolves a stable identifier for a column definition.
     *
     * @param {object} [column={}] - Column definition object.
     * @returns {string|undefined} Column identifier suitable for React keys.
     */
    static getColumnIdentifier(column = {}) {
        return EventTableUtils.isCompoundColumn(column) ? column.keys.join('-') : column.key;
    }

    /**
     * Extracts the normalized market id from a column definition.
     *
     * @param {object} column - Column definition object.
     * @returns {string|null} Normalized market id or null when absent.
     */
    static getColumnMarketId(column) {
        return EventTableUtils.normalizeMarketId(column?.marketId);
    }

    /**
     * Returns the non-odds columns from a table schema.
     *
     * @param {Array<object>} [columns=[]] - Full column schema.
     * @returns {Array<object>} Base standings columns without odds columns.
     */
    static getBaseEventTableColumns(columns = []) {
        return columns.filter(col => !('key' in col && EventTableUtils.isOddsKey(col.key)));
    }

    /**
     * Checks whether a column definition represents a standings statistic.
     *
     * @param {object} [column={}] - Column definition object.
     * @returns {boolean} True when the column is a stat field (not rank/participant or odds).
     */
    static isStatColumn(column = {}) {
        return Boolean(column.key) && !EventTableUtils.isOddsKey(column.key);
    }

    /**
     * Filters standings stat columns to the compact or full set shown in the table.
     * Compact view keeps P, +/- and Pts; All Stats shows every non-odds stat column in the schema.
     *
     * @param {Array<object>} [columns=[]] - Full table column schema.
     * @param {boolean} [showAllStats=false] - When true, show all standings stat columns.
     * @returns {Array<object>} Columns visible for the selected stats mode.
     */
    static filterColumnsByStatsVisibility(columns = [], showAllStats = false) {
        if (!Array.isArray(columns) || !columns.length) {
            return columns || [];
        }

        if (showAllStats) {
            return columns.filter(
                column =>
                    EventTableUtils.isCompoundColumn(column) ||
                    EventTableUtils.isOddsKey(column.key) ||
                    EventTableUtils.isStatColumn(column)
            );
        }

        const compactKeys = new Set(EVENT_TABLE_COMPACT_STAT_FIELD_KEYS);

        return columns.filter(
            column =>
                EventTableUtils.isCompoundColumn(column) ||
                EventTableUtils.isOddsKey(column.key) ||
                compactKeys.has(column.key)
        );
    }

    /**
     * Determines whether the All Stats toggle should be shown for a table schema.
     *
     * @param {Array<object>} [columns=[]] - Full table column schema.
     * @returns {boolean} True when extended-only stats are available in the feed.
     */
    static shouldShowStatsToggle(columns = []) {
        if (!Array.isArray(columns) || !columns.length) {
            return false;
        }

        const compactKeys = new Set(EVENT_TABLE_COMPACT_STAT_FIELD_KEYS);

        return columns.some(
            column => EventTableUtils.isStatColumn(column) && !compactKeys.has(column.key)
        );
    }

    /**
     * Returns only odds columns from a table schema.
     *
     * @param {Array<object>} [columns=[]] - Full column schema.
     * @returns {Array<object>} Odds columns.
     */
    static getOddsEventTableColumns(columns = []) {
        return columns.filter(col => 'key' in col && EventTableUtils.isOddsKey(col.key));
    }

    static sortByDisplayOrder(left, right) {
        return (left?.displayOrder || 0) - (right?.displayOrder || 0);
    }

    static adaptMarketGroupForSelector(group = {}) {
        const marketInfos = [...(group?.marketInfos || [])].sort(
            EventTableUtils.sortByDisplayOrder
        );
        const marketIdsFromInfos = [
            ...new Set(
                marketInfos.flatMap(marketInfo =>
                    (marketInfo?.markets || [])
                        .map(market => EventTableUtils.normalizeMarketId(market?.id))
                        .filter(Boolean)
                )
            ),
        ];
        const marketIds = marketIdsFromInfos.length
            ? marketIdsFromInfos
            : [...(group?.marketIds || [])].map(EventTableUtils.normalizeMarketId).filter(Boolean);

        return {
            ...group,
            marketInfos,
            marketIds,
        };
    }

    static adaptMarketGroupsForSelector(marketGroups = [], filterPredicate = () => true) {
        return [...marketGroups]
            .map(EventTableUtils.adaptMarketGroupForSelector)
            .filter(group => group.marketIds.length > 0 && filterPredicate(group))
            .sort(EventTableUtils.sortByDisplayOrder);
    }

    /**
     * Adapts market groups for a single table selector.
     *
     * @param {Array<object>} [marketGroups=[]] - Raw market group definitions.
     * @param {Array<object>} [columns=[]] - Table column schema.
     * @returns {Array<object>} Enriched market groups available for the table.
     */
    static adaptAggregatedMarketGroupsForSelector(marketGroups = [], columns = []) {
        const availableMarketIds = new Set(
            EventTableUtils.getOddsEventTableColumns(columns)
                .map(EventTableUtils.getColumnMarketId)
                .filter(Boolean)
        );

        return EventTableUtils.adaptMarketGroupsForSelector(marketGroups, group =>
            group.marketIds.some(marketId => availableMarketIds.has(marketId))
        );
    }

    /**
     * Adapts market groups for a shared selector.
     *
     * @param {Array<object>} [marketGroups=[]] - Raw market group definitions.
     * @param {Array<Array<object>>} [columnsByTable=[]] - Column schemas grouped by table.
     * @returns {Array<object>} Shared market groups available across all tables.
     */
    static adaptSharedMarketGroupsForSelector(marketGroups = [], columnsByTable = []) {
        const marketIdSetsByTable = columnsByTable
            .map(
                columns =>
                    new Set(
                        EventTableUtils.getOddsEventTableColumns(columns)
                            .map(EventTableUtils.getColumnMarketId)
                            .filter(Boolean)
                    )
            )
            .filter(marketIds => marketIds.size > 0);

        if (!marketIdSetsByTable.length) {
            return [];
        }

        return EventTableUtils.adaptMarketGroupsForSelector(marketGroups, group =>
            marketIdSetsByTable.every(tableMarketIds =>
                group.marketIds.some(marketId => tableMarketIds.has(marketId))
            )
        );
    }

    /**
     * Builds one selector tab per outright odds column shared across all standings tables.
     * Tabs use real market names (e.g. Top 2, Relegated) instead of event market groups.
     *
     * @param {Array<Array<object>>} [columnsByTable=[]] - Column schemas grouped by table.
     * @returns {Array<object>} Selector entries with a single market id each.
     */
    static adaptSharedOddsMarketsForSelector(columnsByTable = []) {
        const oddsColumnsByTable = columnsByTable
            .map(columns => EventTableUtils.getOddsEventTableColumns(columns))
            .filter(columns => columns.length > 0);

        if (!oddsColumnsByTable.length) {
            return [];
        }

        const sharedMarketIds = oddsColumnsByTable.reduce((sharedIds, columns, index) => {
            const marketIds = new Set(
                columns.map(EventTableUtils.getColumnMarketId).filter(Boolean)
            );

            if (index === 0) {
                return marketIds;
            }

            return new Set([...sharedIds].filter(id => marketIds.has(id)));
        }, new Set());

        if (!sharedMarketIds.size) {
            return [];
        }

        return oddsColumnsByTable[0]
            .filter(column => {
                const marketId = EventTableUtils.getColumnMarketId(column);
                return marketId && sharedMarketIds.has(marketId);
            })
            .map((column, index) => {
                const marketId = EventTableUtils.getColumnMarketId(column);
                return {
                    id: marketId,
                    name: column.header,
                    displayOrder: index,
                    marketIds: [marketId],
                };
            });
    }

    /**
     * Filters table columns to base standings plus a single selected odds market.
     *
     * @param {string|undefined} selectedMarketId - Selected outright market id.
     * @param {Array<object>} [columns=[]] - Full table column schema.
     * @returns {Array<object>} Visible columns for the selected market.
     */
    static filterColumnsBySelectedMarketId(selectedMarketId, columns = []) {
        if (!selectedMarketId) {
            return EventTableUtils.getBaseEventTableColumns(columns);
        }

        const normalizedMarketId = EventTableUtils.normalizeMarketId(selectedMarketId);

        return [
            ...EventTableUtils.getBaseEventTableColumns(columns),
            ...EventTableUtils.getOddsEventTableColumns(columns).filter(
                column => EventTableUtils.getColumnMarketId(column) === normalizedMarketId
            ),
        ];
    }

    /**
     * Resolves the default selected market group id.
     *
     * @param {Array<object>} [marketGroups=[]] - Available market groups.
     * @returns {string|undefined} First available market group id.
     */
    static getDefaultSelectedMarketGroupId(marketGroups = []) {
        return marketGroups[0]?.id;
    }

    /**
     * Filters table columns according to the selected market group.
     *
     * @param {object} selectedMarketGroup - Selected market group definition.
     * @param {Array<object>} [columns=[]] - Full table column schema.
     * @returns {Array<object>} Visible columns for the selected market group.
     */
    static filterColumnsBySelectedMarketGroup(selectedMarketGroup, columns = []) {
        if (!selectedMarketGroup) {
            return columns;
        }

        const allowedMarketIds = new Set(
            (selectedMarketGroup.marketIds || [])
                .map(EventTableUtils.normalizeMarketId)
                .filter(Boolean)
        );

        if (!allowedMarketIds.size) {
            return EventTableUtils.getBaseEventTableColumns(columns);
        }

        return [
            ...EventTableUtils.getBaseEventTableColumns(columns),
            ...EventTableUtils.getOddsEventTableColumns(columns).filter(column => {
                const marketId = EventTableUtils.getColumnMarketId(column);
                return marketId ? allowedMarketIds.has(marketId) : false;
            }),
        ];
    }

    /**
     * Builds column width styles for the event table.
     *
     * @param {Array<object>} columns - Column schema.
     * @param {number} narrowPx - Width for narrow/stat columns.
     * @param {number} oddsPx - Width for odds columns.
     * @returns {Array<object|undefined>} Column style objects in render order.
     */
    static getEventTableColStyles(columns, narrowPx, oddsPx) {
        const out = [];
        const fixedNarrow = { width: narrowPx, minWidth: narrowPx, maxWidth: narrowPx };
        const fixedOdds = { width: oddsPx, minWidth: oddsPx, maxWidth: oddsPx };

        const colStyleForKey = key => {
            if (key === 'countryCode') return undefined;
            if (EventTableUtils.isOddsKey(key)) return fixedOdds;
            return fixedNarrow;
        };

        for (const col of columns) {
            for (const key of EventTableUtils.getColumnKeys(col)) {
                out.push(colStyleForKey(key));
            }
        }

        return out;
    }

    static resolveTranslatedLabel(stringResolver, translationKey, fallbackLabel) {
        if (!translationKey || !stringResolver?.resolveString) {
            return fallbackLabel;
        }

        const resolvedLabel = stringResolver.resolveString(translationKey);

        return resolvedLabel && resolvedLabel !== translationKey ? resolvedLabel : fallbackLabel;
    }

    static getEventTableStatDefinitionEntries(statDefinitions = []) {
        return Array.isArray(statDefinitions) ? statDefinitions : [];
    }

    static getEventTableStatDefinitionMap(statDefinitions = []) {
        return new Map(
            EventTableUtils.getEventTableStatDefinitionEntries(statDefinitions).map(definition => [
                definition.code,
                definition,
            ])
        );
    }

    static getDefaultEventTableColumns(options = {}) {
        const { statDefinitions = [], stringResolver } = options;
        const groupLabel = EventTableUtils.resolveTranslatedLabel(
            stringResolver,
            '$EVENT_TABLE.GROUP',
            'Group'
        );

        return [
            { header: groupLabel, colSpan: 2, keys: [RANK, PARTICIPANT] },
            ...EventTableUtils.getEventTableStatDefinitionEntries(statDefinitions).map(
                definition => ({
                    header: EventTableUtils.resolveTranslatedLabel(
                        stringResolver,
                        definition.labelKey,
                        definition.code
                    ),
                    key: definition.fieldKey,
                })
            ),
        ];
    }

    static iconToCountryCode(icon) {
        return icon ? icon.replace(/^flags\//, '').replace(/\.svg$/, '') : '';
    }

    static resolveFeedText(value) {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'object' && value.value !== undefined) {
            return String(value.value);
        }

        return String(value);
    }

    static getRenderableStages(tournament) {
        return (tournament?.stages || []).filter(
            stage => stage?.displayed !== false && (stage?.standings || []).length > 0
        );
    }

    /**
     * Collects the unique market ids referenced by a single league stage.
     * Market references live on the standings rows' participant selections.
     *
     * @param {object} stage - Tournament league stage.
     * @returns {Array<string>} Unique market ids referenced by the stage.
     */
    static getStageMarketIds(stage) {
        const ids = new Set();
        (stage?.standings || []).forEach(row => {
            (row.selections || []).forEach(selection => {
                if (selection?.marketId !== null && selection?.marketId !== undefined) {
                    ids.add(String(selection.marketId));
                }
            });
        });
        return [...ids];
    }

    /**
     * Resolves the parent event ids for markets referenced by a league stage standings selections.
     *
     * @param {object} stage - Tournament league stage.
     * @param {Array<object>} [storeMarkets=[]] - Markets from the store.
     * @returns {Array<string>} Unique parent event ids for the stage markets.
     */
    static getStageEventIds(stage, storeMarkets = []) {
        const marketById = new Map(
            (storeMarkets || [])
                .filter(Boolean)
                .map(market => [String(market.mktId ?? market.id), market])
        );

        const eventIds = new Set();
        for (const marketId of EventTableUtils.getStageMarketIds(stage)) {
            const market = marketById.get(String(marketId));
            const eventId = market?.eventNodeId ?? market?.eventId;
            if (eventId !== null && eventId !== undefined) {
                eventIds.add(String(eventId));
            }
        }

        return [...eventIds];
    }

    /**
     * Builds a synthetic event group for fixtures under a stage table from stage market events.
     *
     * @param {object} stage - Tournament league stage.
     * @param {Array<object>} [storeMarkets=[]] - Markets from the store.
     * @param {string} [groupLabel] - Display label for the fixtures group.
     * @returns {Array<object>} Event group definitions for {@link Mojito.Modules.EventsCoupon}.
     */
    static buildStageFixtureEventGroups(stage, storeMarkets = [], groupLabel) {
        const eventIds = EventTableUtils.getStageEventIds(stage, storeMarkets);
        if (!eventIds.length) {
            return [];
        }

        return [
            {
                id: `table-stage-${stage.id}`,
                typeName: groupLabel || EventTableUtils.resolveFeedText(stage?.name),
                events: eventIds.map(id => ({ id })),
            },
        ];
    }

    static collectParticipantAliases(participant) {
        return [participant?.name, participant?.shortName, participant?.displayName]
            .map(EventTableUtils.resolveFeedText)
            .map(value => value.trim().toLowerCase())
            .filter(Boolean);
    }

    static getStageParticipantNameSet(stage, storeMarkets = []) {
        const markets = EventTableUtils.adaptStoreMarketsToEventTableMarkets(storeMarkets);
        const participantsById = EventTableUtils.buildStageParticipantsById(stage, markets);
        const names = new Set();

        participantsById.forEach(participant => {
            EventTableUtils.collectParticipantAliases(participant).forEach(alias =>
                names.add(alias)
            );
        });

        return names;
    }

    static getStageParticipantIdSet(stage, storeMarkets = []) {
        const markets = EventTableUtils.adaptStoreMarketsToEventTableMarkets(storeMarkets);
        const ids = new Set(
            [...EventTableUtils.buildStageParticipantsById(stage, markets).keys()].map(String)
        );

        (stage?.standings || []).forEach(row => {
            if (row?.id !== null && row?.id !== undefined) {
                ids.add(String(row.id));
            }
        });

        return ids;
    }

    /**
     * Returns true when every event participant belongs to the stage participant list.
     *
     * @param {object} event - Match event.
     * @param {object} stage - Tournament league stage.
     * @param {Array<object>} [storeMarkets=[]] - Markets from the store.
     * @returns {boolean} Whether the event belongs to the stage.
     */
    static eventBelongsToStage(event, stage, storeMarkets = []) {
        const stageParticipantNames = EventTableUtils.getStageParticipantNameSet(
            stage,
            storeMarkets
        );
        const stageParticipantIds = EventTableUtils.getStageParticipantIdSet(stage, storeMarkets);
        const eventParticipants = event?.participants || [];

        if (eventParticipants.length < 2) {
            return false;
        }

        const eventParticipantIds = eventParticipants
            .map(participant => String(participant?.id ?? participant?.participantId ?? ''))
            .filter(Boolean);

        if (
            eventParticipantIds.length >= 2 &&
            eventParticipantIds.every(id => stageParticipantIds.has(id))
        ) {
            return true;
        }

        if (!stageParticipantNames.size) {
            return false;
        }

        return (
            eventParticipants.length >= 2 &&
            eventParticipants.every(participant =>
                EventTableUtils.collectParticipantAliases(participant).some(alias =>
                    stageParticipantNames.has(alias)
                )
            )
        );
    }

    static isFixtureFinished(event) {
        return Boolean(event?.ended);
    }

    static sortFixtureEvents(events = []) {
        return [...events].sort((left, right) => {
            const leftFinished = EventTableUtils.isFixtureFinished(left);
            const rightFinished = EventTableUtils.isFixtureFinished(right);

            if (leftFinished !== rightFinished) {
                return leftFinished ? -1 : 1;
            }

            const leftTime = left?.startTime ? new Date(left.startTime).getTime() : 0;
            const rightTime = right?.startTime ? new Date(right.startTime).getTime() : 0;
            return leftTime - rightTime;
        });
    }

    /**
     * Resolves the match fixtures shown under a group standings table.
     *
     * Fixtures are league match events whose participants both belong to the stage, or match
     * events directly linked via the stage market chain.
     *
     * @param {object} stage - Tournament league stage.
     * @param {Array<object>} [storeMarkets=[]] - Markets from the store.
     * @param {Object<string, object>} [eventsById={}] - Subscribed events keyed by id.
     * @returns {Array<object>} Match events for the stage fixtures list.
     */
    static getStageFixtureEvents(stage, storeMarkets = [], eventsById = {}) {
        const events = Object.values(eventsById || {}).filter(Boolean);
        const matchEvents = events.filter(event => EventsUtils.isMatchEventType(event.eventType));
        const stageEventIds = new Set(
            EventTableUtils.getStageEventIds(stage, storeMarkets).map(String)
        );
        const fixturesById = new Map();

        matchEvents.forEach(event => {
            const belongsToStage =
                stageEventIds.has(String(event.id)) ||
                EventTableUtils.eventBelongsToStage(event, stage, storeMarkets);

            if (belongsToStage) {
                fixturesById.set(String(event.id), event);
            }
        });

        return EventTableUtils.sortFixtureEvents([...fixturesById.values()]);
    }

    /**
     * Resolves stage participants used to render fixture rows.
     *
     * @param {object} stage - Tournament league stage.
     * @param {Array<object>} [storeMarkets=[]] - Markets from the store.
     * @returns {Array<object>} Stage participants for fixture rendering.
     */
    static getStageParticipantsForFixtures(stage, storeMarkets = []) {
        const markets = EventTableUtils.adaptStoreMarketsToEventTableMarkets(storeMarkets);
        return [...EventTableUtils.buildStageParticipantsById(stage, markets).values()];
    }

    /**
     * Collects the unique market ids referenced across every stage of a tournament.
     *
     * @param {object} tournament - Tournament data object.
     * @returns {Array<string>} Unique market ids referenced by the tournament.
     */
    static getTournamentMarketIds(tournament) {
        const ids = new Set();
        (tournament?.stages || []).forEach(stage => {
            EventTableUtils.getStageMarketIds(stage).forEach(id => ids.add(id));
        });
        return [...ids];
    }

    static extractGroupKey(stage) {
        const match = /Group\s+([A-Z0-9]+)\s*$/i.exec(EventTableUtils.resolveFeedText(stage?.name));
        return match ? match[1] : null;
    }

    /**
     * Extracts a clean stage sub-name from a fully-qualified backend heading such as
     * `League standings - England, Premier League 2026/27, stage: Regular Season`.
     * Returns the substring after `stage:` when present; otherwise `null`.
     *
     * @param {object} stage - Tournament league stage.
     * @returns {string|null} Trimmed stage sub-name or null.
     */
    static extractStageSubName(stage) {
        const name = EventTableUtils.resolveFeedText(stage?.name);
        const marker = ', stage:';
        const start = name.toLowerCase().indexOf(marker);
        if (start === -1) return null;
        const subName = name.slice(start + marker.length).trim();
        return subName || null;
    }

    /**
     * Simplifies a market display name by stripping a leading tournament-name segment.
     * Backend feeds often emit market names as `<Tournament> - <Market>` (e.g.
     * `Premier League - Winner`); the tournament name is already shown as the page title,
     * so the column header only needs the market part (`Winner`).
     *
     * @param {string} marketName - Original market name.
     * @param {string} [tournamentName] - Tournament name to strip from the prefix.
     * @returns {string} Simplified market label.
     */
    static simplifyMarketLabel(marketName, tournamentName) {
        const label = String(marketName || '').trim();
        if (!label) {
            return '';
        }

        if (tournamentName) {
            const prefix = `${String(tournamentName).trim()} - `;
            if (label.toLowerCase().startsWith(prefix.toLowerCase())) {
                return label.slice(prefix.length).trim() || label;
            }
        }

        const dashIndex = label.indexOf(' - ');
        if (dashIndex > 0 && dashIndex < label.length - 3) {
            return label.slice(dashIndex + 3).trim() || label;
        }

        return label;
    }

    /**
     * Resolves the heading shown above a stage's standings table.
     *
     * When the stage name follows the `Group X` convention we render a localized
     * `Group X` label; otherwise the feed-provided stage name (e.g. `League A`) is used
     * verbatim, as it already arrives localized from the tournament slice.
     *
     * @param {object} stage - Tournament league stage.
     * @param {object} [stringResolver] - String resolver used to localize the `Group` prefix.
     * @returns {string} Display label for the stage.
     */
    static getStageDisplayLabel(stage, stringResolver) {
        const groupKey = EventTableUtils.extractGroupKey(stage);
        if (groupKey !== null && groupKey !== undefined) {
            const groupPrefix = EventTableUtils.resolveTranslatedLabel(
                stringResolver,
                '$EVENT_TABLE.GROUP',
                'Group'
            );
            return `${groupPrefix} ${groupKey}`;
        }
        const subName = EventTableUtils.extractStageSubName(stage);
        if (subName) {
            return subName;
        }
        return stage?.name || '';
    }

    static createSelectionButtonEvent(tournament) {
        return {
            id: `tournament-${tournament.id}`,
            name: tournament.name,
            sportId: tournament.sportId,
            classId: String(tournament.classId || ''),
            typeId: String(tournament.typeId || tournament.id),
            typeName: tournament.name,
            eventType: 'OUTRIGHT',
            status: 'ACTIVE',
            homeAway: false,
            ended: false,
            details: {
                inPlay: false,
                allowInplay: false,
            },
            participants: [],
        };
    }

    static createSelectionButtonCellData({
        tournament,
        participant,
        market,
        selectionId,
        odds,
        storeSelection,
    }) {
        if (!participant || !market || odds === null || odds === undefined) {
            return null;
        }

        const marketId = String(market.mktId);
        const prices =
            storeSelection?.prices?.length > 0
                ? storeSelection.prices
                : [
                      {
                          id: `${selectionId}-price`,
                          type: 'LP',
                          selectionId,
                          decimalLabel: String(odds),
                      },
                  ];

        return {
            event: EventTableUtils.createSelectionButtonEvent(tournament),
            market: {
                id: marketId,
                status: market.status || 'ACTIVE',
                type: market.type || 'OUTRIGHT',
                name: market.label,
            },
            selection: {
                id: selectionId,
                status: storeSelection?.status || 'ACTIVE',
                disabled: false,
                name: storeSelection?.name || participant.name,
                shortName: storeSelection?.shortName || participant.shortName || participant.name,
                canonicalName: storeSelection?.name || participant.name,
                handicapLabel: '',
                type: market.label,
                marketId,
                prices,
            },
            displayOdds: String(odds),
        };
    }

    static createUnavailableMarketCellData({ tournament, participant, market, selectionId }) {
        if (!tournament || !market) {
            return null;
        }

        const marketId = String(market.mktId ?? market.id);
        let resolvedSelectionId = marketId;
        if (selectionId) {
            resolvedSelectionId = String(selectionId);
        } else if (participant) {
            resolvedSelectionId = String(participant.id);
        }

        return {
            event: EventTableUtils.createSelectionButtonEvent(tournament),
            market: {
                id: marketId,
                status: market.status || 'ACTIVE',
                type: market.type || 'OUTRIGHT',
                name: market.label,
            },
            selection: {
                id: resolvedSelectionId,
                status: 'ACTIVE',
                disabled: false,
                name: participant?.name,
                shortName: participant?.shortName || participant?.name,
                canonicalName: participant?.name,
                handicapLabel: '',
                type: market.label,
                marketId,
                prices: [],
            },
        };
    }

    /**
     * Normalizes a zone colour from the Genbet feed into a CSS-compatible value.
     *
     * Genbet may deliver values with or without a leading `#`, for example `#008000`
     * or `FF0000`.
     *
     * @param {string} [colour] - Raw zone colour from the feed.
     * @returns {string} Normalized colour or an empty string when unavailable.
     */
    static normalizeZoneColour(colour) {
        if (colour === null || colour === undefined) {
            return '';
        }

        const normalized = String(colour).trim();
        if (!normalized) {
            return '';
        }

        if (normalized.startsWith('#')) {
            return normalized;
        }

        if (/^[0-9A-Fa-f]{3,8}$/.test(normalized)) {
            return `#${normalized}`;
        }

        return normalized;
    }

    /**
     * Resolves standings zone metadata from a league row.
     *
     * Genbet delivers `zone` and `zone_color` on `LeagueRow`, which the backend maps to
     * `zone: { name, zoneColor }` in tournament JSON. This helper accepts the nested object
     * shape as well as snake_case and legacy aliases.
     *
     * @param {object} [row] - Standings row from a tournament league stage.
     * @returns {{zoneName: string, zoneColour: string}} Resolved zone label and colour.
     */
    static resolveStandingsZoneMetadata(row) {
        const zone = row?.zone;

        if (typeof zone === 'string') {
            return {
                zoneName: EventTableUtils.resolveFeedText(zone),
                zoneColour: EventTableUtils.normalizeZoneColour(
                    row?.zone_color || row?.zoneColor || row?.zone_colour
                ),
            };
        }

        if (zone && typeof zone === 'object') {
            return {
                zoneName: EventTableUtils.resolveFeedText(zone.name),
                zoneColour: EventTableUtils.normalizeZoneColour(
                    zone.zoneColor || zone.zoneColour || zone.zone_colour || zone.zone_color
                ),
            };
        }

        return {
            zoneName: EventTableUtils.resolveFeedText(row?.zone_name || row?.zoneName),
            zoneColour: EventTableUtils.normalizeZoneColour(
                row?.zone_color || row?.zoneColor || row?.zone_colour
            ),
        };
    }

    /**
     * Transforms a standings row into the normalized event-table row format.
     *
     * @param {object} row - Standings row from a tournament league stage.
     * @param {Map<string, object>} participantsById - Participant lookup indexed by participant id.
     * @param {object} [options={}] - EventTable adapter options.
     * @returns {object} Normalized table row.
     */
    static transformStandingsRowToEventTableRow(row, participantsById, options = {}) {
        const statDefinitionMap = EventTableUtils.getEventTableStatDefinitionMap(
            options.statDefinitions
        );
        const statValues = EventTableUtils.getEventTableStatDefinitionEntries(
            options.statDefinitions
        ).reduce(
            (accumulator, definition) => ({
                ...accumulator,
                [definition.fieldKey]: '',
            }),
            {}
        );
        const participant = participantsById.get(String(row.id));
        const icon = participant ? EventTableUtils.resolveFeedText(participant.icon) : '';
        const participantIcon = isParticipantIconUrl(icon) ? icon : '';
        let countryCode = '';
        let rank;
        if (participant && !participantIcon) {
            countryCode = EventTableUtils.iconToCountryCode(participant.icon);
        }
        const { zoneName, zoneColour } = EventTableUtils.resolveStandingsZoneMetadata(row);

        for (const statistic of row.statistics || []) {
            const fieldKey = statDefinitionMap.get(statistic.type)?.fieldKey;
            if (statistic.type === 'POS' && !isNaN(+statistic.value))
                rank = Number(statistic.value);
            if (fieldKey) {
                statValues[fieldKey] = statistic.value;
            }
        }

        return {
            rowId: String(row.id),
            rank: rank ?? 0,
            countryCode,
            participantIcon,
            countryName: participant ? EventTableUtils.resolveFeedText(participant.name) : '',
            countryShortName: participant
                ? EventTableUtils.resolveFeedText(participant.shortName || participant.name)
                : '',
            zoneName,
            zoneColour,
            sportId: '',
            ...statValues,
            marketCells: {},
        };
    }

    /**
     * Builds the visible base column schema for a tournament league stage standings table.
     *
     * @param {object} stage - Tournament league stage.
     * @param {string} [groupLabel] - Fully-resolved stage/group label (e.g. `Group A`,
     * `Regular Season`); used verbatim as the first column header.
     * @param {object} [options={}] - EventTable adapter options.
     * @returns {Array<object>} Table column schema for the stage.
     */
    static getEventTableColumnsFromStage(stage, groupLabel, options = {}) {
        const firstRow = (stage?.standings || [])[0];
        const { statDefinitions = [], stringResolver } = options;

        if (!firstRow) {
            return EventTableUtils.getDefaultEventTableColumns({ statDefinitions, stringResolver });
        }

        const presentStatTypes = new Set(
            (firstRow.statistics || []).map(statistic => statistic.type)
        );
        const statsColumns = EventTableUtils.getEventTableStatDefinitionEntries(statDefinitions)
            .filter(definition => presentStatTypes.has(definition.code))
            .map(definition => ({
                header: EventTableUtils.resolveTranslatedLabel(
                    stringResolver,
                    definition.labelKey,
                    definition.code
                ),
                key: definition.fieldKey,
            }));

        const firstColumnHeader =
            groupLabel !== null && groupLabel !== undefined && groupLabel !== ''
                ? groupLabel
                : EventTableUtils.getStageDisplayLabel(stage, stringResolver);

        return [
            { header: firstColumnHeader, colSpan: 2, keys: [RANK, PARTICIPANT] },
            ...statsColumns,
        ];
    }

    static buildMarketOddsColumns(marketIds, markets, tournamentName) {
        const marketById = new Map(
            markets.map(market => [String(market.mktId ?? market.id), market])
        );
        const columns = [];
        const marketFieldMap = {};

        marketIds.forEach((marketId, idx) => {
            const market = marketById.get(String(marketId));
            if (market) {
                const fieldName = `marketOdds${idx + 1}`;
                const label =
                    market.label ||
                    (typeof market.name === 'string' ? market.name : market.name?.value || '');
                columns.push({
                    header: EventTableUtils.simplifyMarketLabel(label, tournamentName),
                    key: fieldName,
                    marketId: String(marketId),
                });
                marketFieldMap[String(marketId)] = fieldName;
            }
        });

        return { columns, marketFieldMap };
    }

    static enrichRowWithMarketOdds(
        row,
        standingsRow,
        marketFieldMap,
        markets,
        tournament,
        participantsById
    ) {
        const marketById = new Map(
            markets.map(market => [String(market.mktId ?? market.id), market])
        );
        const participant = participantsById.get(String(standingsRow.id));
        const enrichedFields = new Set();

        for (const selection of standingsRow.selections || []) {
            const marketId = String(selection.marketId);
            const fieldName = marketFieldMap[marketId];
            if (!fieldName) continue;
            const market = marketById.get(marketId);
            if (!market) continue;

            enrichedFields.add(fieldName);

            let storeSelection = market.selectionsById?.[String(selection.selectionId)];
            if (!storeSelection && participant) {
                storeSelection = Object.values(market.selectionsById || {}).find(
                    storeItem =>
                        String(storeItem.participantId) === String(participant.id) ||
                        storeItem.name === participant.name
                );
            }

            const selectionId = String(storeSelection?.id ?? selection.selectionId ?? '');
            row[fieldName] = storeSelection?.odds ?? MISSING_ODDS_DISPLAY;

            const cellData = EventTableUtils.createSelectionButtonCellData({
                tournament,
                participant,
                market,
                selectionId,
                odds: storeSelection?.odds,
                storeSelection,
            });
            if (cellData) {
                row.marketCells[fieldName] = cellData;
            } else {
                const unavailableCellData = EventTableUtils.createUnavailableMarketCellData({
                    tournament,
                    participant,
                    market,
                    selectionId,
                });
                if (unavailableCellData) {
                    row.marketCells[fieldName] = unavailableCellData;
                }
            }
        }

        for (const [marketId, fieldName] of Object.entries(marketFieldMap)) {
            if (enrichedFields.has(fieldName)) {
                continue;
            }

            row[fieldName] = MISSING_ODDS_DISPLAY;
            const unavailableCellData = EventTableUtils.createUnavailableMarketCellData({
                tournament,
                participant,
                market: marketById.get(String(marketId)),
            });
            if (unavailableCellData) {
                row.marketCells[fieldName] = unavailableCellData;
            }
        }

        return row;
    }

    static normalizeMarketsPayload(marketsPayload) {
        const payload =
            marketsPayload && typeof marketsPayload === 'object' && 'default' in marketsPayload
                ? marketsPayload.default
                : marketsPayload;

        return Array.isArray(payload?.markets) ? payload.markets : [];
    }

    static normalizeLeagueResponse(response) {
        const payload =
            response && typeof response === 'object' && 'default' in response
                ? response.default
                : response;

        return Array.isArray(payload?.tournaments) ? payload : { tournaments: [] };
    }

    /**
     * Adapts markets coming from the SportsContent markets store (IMarket shape) into the
     * simplified shape consumed by {@link Mojito.Modules.EventTable.utils#buildMarketOddsColumns|buildMarketOddsColumns}
     * and {@link Mojito.Modules.EventTable.utils#enrichRowWithMarketOdds|enrichRowWithMarketOdds}.
     *
     * Standings rows link to a store selection through `selectionId`, so selections are indexed
     * by id to allow a direct odds lookup per participant selection.
     *
     * @param {Array<object>} [storeMarkets=[]] - Markets from the store.
     * @returns {Array<object>} Markets in `{ mktId, label, status, type, selectionsById }` shape.
     */
    static adaptStoreMarketsToEventTableMarkets(storeMarkets = []) {
        const resolveLabel = market =>
            typeof market.name === 'string'
                ? market.name
                : market.name?.value || market.label || '';

        return (storeMarkets || []).filter(Boolean).map(market => {
            const selections = market.selections || [];
            return {
                mktId: String(market.mktId ?? market.id),
                label: resolveLabel(market),
                status: market.status,
                type: market.type,
                selectionsById: Object.fromEntries(
                    selections.map(selection => {
                        const id = String(selection._id ?? selection.id);
                        return [
                            id,
                            {
                                id,
                                name: selection.name,
                                shortName: selection.shortName,
                                participantId: selection.participantId,
                                odds: selection.odds ?? selection.prices?.[0]?.decimalLabel,
                                status: selection.status,
                                prices: selection.prices,
                            },
                        ];
                    })
                ),
            };
        });
    }

    /**
     * Builds a participant lookup for a league stage, merging any inline participants declared
     * on the stage with a fallback that resolves team metadata from the standings rows'
     * outright-market selections. LEAGUE tournaments (e.g. Premier League) frequently ship an
     * empty `stage.participants` array; team names must then come from the subscribed outright
     * market (each selection carries the team name).
     *
     * @param {object} stage - Tournament league stage.
     * @param {Array<object>} [markets=[]] - Markets adapted for the event table.
     * @param {Array<object>} [tournamentParticipants=[]] - Tournament-level participants from Genbet.
     * @returns {Map<string, object>} Participant lookup keyed by standings row id.
     */
    static buildStageParticipantsById(stage, markets = [], tournamentParticipants = []) {
        const participantsById = new Map();
        const mergeParticipant = (existing, participant) => {
            if (!existing) {
                return participant;
            }

            return {
                ...existing,
                ...participant,
                icon: preferParticipantIcon(existing.icon, participant.icon),
                name: EventTableUtils.resolveFeedText(participant.name || existing.name),
                shortName: EventTableUtils.resolveFeedText(
                    participant.shortName || existing.shortName || participant.name || existing.name
                ),
            };
        };

        (tournamentParticipants || []).forEach(participant => {
            participantsById.set(String(participant.id), participant);
        });

        (stage?.participants || []).forEach(participant => {
            const id = String(participant.id);
            participantsById.set(id, mergeParticipant(participantsById.get(id), participant));
        });

        if (!markets.length) {
            return participantsById;
        }

        const marketById = new Map(
            markets.map(market => [String(market.mktId ?? market.id), market])
        );

        for (const row of stage?.standings || []) {
            const rowId = String(row.id);
            const existing = participantsById.get(rowId);
            if (existing?.name) {
                continue;
            }
            for (const selection of row.selections || []) {
                const market = marketById.get(String(selection?.marketId));
                const storeSelection = market?.selectionsById?.[String(selection?.selectionId)];
                const teamName = storeSelection?.name;
                if (teamName) {
                    participantsById.set(
                        rowId,
                        mergeParticipant(existing, {
                            id: row.id,
                            name: EventTableUtils.resolveFeedText(teamName),
                            shortName: EventTableUtils.resolveFeedText(
                                storeSelection.shortName || teamName
                            ),
                            icon: '',
                        })
                    );
                    break;
                }
            }
        }

        return participantsById;
    }

    /**
     * Transforms tournament data into EventTable data and columns grouped per league stage.
     *
     * @param {object} response - Raw standings response containing tournaments.
     * @param {object} marketsPayload - Raw market payload used to enrich odds columns.
     * @param {object} [options={}] - EventTable adapter options.
     * @returns {object} EventTable-ready data grouped by stage group key.
     */
    static getEventTableDataAndColumnsFromLeagues(response, marketsPayload, options = {}) {
        const out = {};
        const leagueResponse = EventTableUtils.normalizeLeagueResponse(response);
        const markets = EventTableUtils.adaptStoreMarketsToEventTableMarkets(
            EventTableUtils.normalizeMarketsPayload(marketsPayload)
        );

        for (const tournament of leagueResponse.tournaments) {
            for (const stage of EventTableUtils.getRenderableStages(tournament)) {
                const groupKey = EventTableUtils.extractGroupKey(stage) || String(stage.id);
                const marketIds = EventTableUtils.getStageMarketIds(stage);
                const { columns: marketOddsColumns, marketFieldMap } =
                    EventTableUtils.buildMarketOddsColumns(marketIds, markets, tournament.name);

                const participantsById = EventTableUtils.buildStageParticipantsById(
                    stage,
                    markets,
                    tournament.participants || []
                );
                const data = (stage.standings || [])
                    .map(row => {
                        const baseRow = EventTableUtils.transformStandingsRowToEventTableRow(
                            row,
                            participantsById,
                            options
                        );
                        baseRow.sportId = tournament.sportId;
                        return EventTableUtils.enrichRowWithMarketOdds(
                            baseRow,
                            row,
                            marketFieldMap,
                            markets,
                            tournament,
                            participantsById
                        );
                    })
                    .sort((rowA, rowB) => rowA.rank - rowB.rank);

                const stageLabel = EventTableUtils.getStageDisplayLabel(
                    stage,
                    options.stringResolver
                );
                const baseColumns = EventTableUtils.getEventTableColumnsFromStage(
                    stage,
                    stageLabel,
                    options
                );
                out[groupKey] = {
                    data,
                    columns: [...baseColumns, ...marketOddsColumns],
                    name: stage.name,
                    label: stageLabel,
                    id: stage.id,
                    stage,
                };
            }
        }
        return out;
    }
}
